import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiCheckCircle, FiClock, FiShield, FiXCircle } from 'react-icons/fi';
import API from '../api';
import selfieImage from '../image/selfie-image.png';

const getSupportedVideoMimeType = () => {
  if (typeof MediaRecorder === 'undefined') return '';
  const candidates = [
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
    'video/mp4'
  ];
  return candidates.find(type => MediaRecorder.isTypeSupported(type)) || '';
};

const SELFIE_PHRASES = {
  mg: {
    label: 'Malagasy',
    text: "Manamarina aho fa marina, tsy misy diso, ary mifanaraka amin'ny kara-panondro izay tazomiko ireo mombamomba ahy ao amin'ny site Adabo Cash. Tsy noterena aho na notambazana asa na tolotra mba hanao izao fanamarinana izao, ary izaho ihany no nanokatra ny kaontiko Adabo Cash."
  },
  fr: {
    label: 'Français',
    text: "Je confirme que les informations me concernant sur le site Adabo Cash sont exactes, sans erreur, et correspondent à la CIN que je tiens. Je n'ai pas été contraint(e), ni attiré(e) par une promesse d'emploi ou de service pour effectuer cette vérification, et j'ai moi-même ouvert mon compte Adabo Cash."
  }
};

const VERIFICATION_STEPS = [
  { title: 'Informations', description: 'Nom et date de naissance' },
  { title: 'CIN', description: 'Recto et verso de la CIN' },
  { title: 'Selfie vidéo', description: 'CIN + phrase à lire' },
  { title: 'Résumé', description: 'Vérifier et envoyer' }
];

const SELFIE_VIDEO_CONSTRAINTS = {
  video: {
    width: { ideal: 640, max: 640 },
    height: { ideal: 480, max: 480 },
    frameRate: { ideal: 15, max: 20 },
    facingMode: 'user',
  },
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
  },
};

const SELFIE_RECORDER_OPTIONS = {
  videoBitsPerSecond: 450000,
  audioBitsPerSecond: 64000,
};

const SELFIE_MAX_RECORDING_DURATION_MS = 30000;
const SELFIE_MAX_RECORDING_DURATION_SECONDS = SELFIE_MAX_RECORDING_DURATION_MS / 1000;
const SELFIE_DURATION_LIMIT_MESSAGE = `La vidéo selfie ne doit pas dépasser ${SELFIE_MAX_RECORDING_DURATION_SECONDS} secondes.`;

const VerificationPage = ({ setPath, setMenu }) => {
  const params = new URLSearchParams(window.location.search);
  const returnTo = params.get('returnTo') || '/dashboard';
  const action = params.get('action') || '';
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [idFront, setIdFront] = useState(null);
  const [idBack, setIdBack] = useState(null);
  const [selfieVideo, setSelfieVideo] = useState(null);
  const [selfieVideoUrl, setSelfieVideoUrl] = useState('');
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [verificationStatus, setVerificationStatus] = useState('LOADING');
  const [selfieLanguage, setSelfieLanguage] = useState('mg');
  const [currentStep, setCurrentStep] = useState(0);
  const [showVerificationIntro, setShowVerificationIntro] = useState(true);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const recordingLimitTimerRef = useRef(null);

  const navigateTo = (nextPath) => {
    const pathOnly = nextPath.startsWith('/') ? nextPath : '/dashboard';
    const menuByPath = {
      '/dashboard': 'dashboard',
      '/deposit': 'deposit',
      '/withdraw': 'withdraw',
      '/cards': 'cards',
      '/transactions': 'transactions',
      '/exchange': 'exchange',
      '/contact': 'contact',
    };
    if (menuByPath[pathOnly]) {
      setMenu(menuByPath[pathOnly]);
    }
    window.history.pushState(null, '', pathOnly);
    setPath(pathOnly);
  };

  const clearRecordingLimitTimer = () => {
    if (recordingLimitTimerRef.current) {
      clearTimeout(recordingLimitTimerRef.current);
      recordingLimitTimerRef.current = null;
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsCameraReady(false);
  };

  useEffect(() => {
    return () => {
      clearRecordingLimitTimer();
      stopCamera();
    };
  }, []);

  useEffect(() => {
    if (!selfieVideo) {
      setSelfieVideoUrl('');
      return undefined;
    }
    const previewUrl = URL.createObjectURL(selfieVideo);
    setSelfieVideoUrl(previewUrl);
    return () => URL.revokeObjectURL(previewUrl);
  }, [selfieVideo]);

  useEffect(() => {
    if (!videoRef.current || !streamRef.current || selfieVideoUrl) return;
    videoRef.current.srcObject = streamRef.current;
  }, [isCameraReady, selfieVideoUrl]);

  useEffect(() => {
    API.get('/verification/')
      .then((res) => setVerificationStatus(res.data?.status || 'NOT_SUBMITTED'))
      .catch((err) => {
        console.error(err);
        setVerificationStatus('NOT_SUBMITTED');
      });
  }, []);

  const startCamera = async () => {
    setCameraError('');
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setCameraError("L'enregistrement vidéo n'est pas supporté par ce navigateur.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia(SELFIE_VIDEO_CONSTRAINTS);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setIsCameraReady(true);
    } catch (err) {
      console.error(err);
      setCameraError("Impossible d'accéder à la caméra. Veuillez autoriser l'accès caméra et micro.");
    }
  };

  const startRecording = async () => {
    setSelfieVideo(null);
    clearRecordingLimitTimer();
    if (!streamRef.current) {
      await startCamera();
      if (!streamRef.current) return;
    }

    chunksRef.current = [];
    const mimeType = getSupportedVideoMimeType();
    const recorder = new MediaRecorder(streamRef.current, {
      ...SELFIE_RECORDER_OPTIONS,
      ...(mimeType ? { mimeType } : {}),
    });
    recorderRef.current = recorder;
    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };
    recorder.onstop = () => {
      clearRecordingLimitTimer();
      const type = recorder.mimeType || 'video/webm';
      const extension = type.includes('mp4') ? 'mp4' : 'webm';
      const blob = new Blob(chunksRef.current, { type });
      setSelfieVideo(new File([blob], `selfie-video.${extension}`, { type }));
      stopCamera();
    };
    recorder.start(1000);
    setIsRecording(true);
    recordingLimitTimerRef.current = setTimeout(() => {
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        toast.info(`${SELFIE_DURATION_LIMIT_MESSAGE} L'enregistrement a été arrêté automatiquement.`);
        stopRecording();
      }
    }, SELFIE_MAX_RECORDING_DURATION_MS);
  };

  const stopRecording = () => {
    clearRecordingLimitTimer();
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.requestData();
      recorderRef.current.stop();
    }
    setIsRecording(false);
  };

  const validateStep = (stepIndex = currentStep) => {
    if (stepIndex === 0 && (!firstName.trim() || !lastName.trim() || !birthDate)) {
      toast.error('Veuillez ajouter votre nom complet et votre date de naissance.');
      return false;
    }
    if (stepIndex === 1 && (!idFront || !idBack)) {
      toast.error('Veuillez ajouter le recto et le verso de votre CIN.');
      return false;
    }
    if (stepIndex === 2 && !selfieVideo) {
      toast.error('Veuillez enregistrer votre vidéo selfie.');
      return false;
    }
    return true;
  };

  const goToNextStep = () => {
    if (!validateStep()) return;
    setCurrentStep(step => Math.min(step + 1, VERIFICATION_STEPS.length - 1));
  };

  const goToPreviousStep = () => {
    setCurrentStep(step => Math.max(step - 1, 0));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (currentStep !== VERIFICATION_STEPS.length - 1) {
      return;
    }
    if (!firstName.trim() || !lastName.trim() || !birthDate) {
      toast.error('Veuillez ajouter votre nom complet et votre date de naissance.');
      return;
    }
    if (!idFront || !idBack || !selfieVideo) {
      toast.error("Veuillez ajouter le recto, le verso et enregistrer la vidéo selfie.");
      return;
    }
    const formData = new FormData();
    formData.append('first_name', firstName.trim());
    formData.append('last_name', lastName.trim());
    formData.append('birth_date', birthDate);
    formData.append('id_front', idFront);
    formData.append('id_back', idBack);
    formData.append('selfie_video', selfieVideo);

    setIsUploading(true);
    try {
      await API.post('/verification/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Vérification envoyée avec succès.');
      setVerificationStatus('PENDING');
      setCurrentStep(0);
      setShowVerificationIntro(true);
      setFirstName('');
      setLastName('');
      setBirthDate('');
      setIdFront(null);
      setIdBack(null);
      setSelfieVideo(null);
      if (selfieVideoUrl) {
        URL.revokeObjectURL(selfieVideoUrl);
        setSelfieVideoUrl('');
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Échec de l'envoi de la vérification.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <section className="verification-page">
      <button type="button" className="back-to-cards-btn verification-back-btn" onClick={() => navigateTo(returnTo)}>
        <FiArrowLeft /> Retour
      </button>

      <div className="verification-page-card">
        {!showVerificationIntro && (
          <>
            <div className="verification-page-header">
              <div className="verification-page-icon">
                <FiShield />
              </div>
              <div>
                <h2>Vérification d'identité</h2>
                <p>
                  Envoyez le recto et le verso de votre CIN, puis enregistrez une courte vidéo selfie en direct.
                </p>
              </div>
            </div>

            <div className="verification-steps">
              {VERIFICATION_STEPS.map((step, index) => (
                <span
                  key={step.title}
                  className={`${index === currentStep ? 'is-active' : ''}${index < currentStep ? ' is-complete' : ''}`}
                >
                  <span className="verification-step-number">
                    {index < currentStep ? <FiCheckCircle /> : index + 1}
                  </span>
                  <span className="verification-step-copy">
                    <strong>{step.title}</strong>
                    <small>{step.description}</small>
                  </span>
                </span>
              ))}
            </div>
          </>
        )}

        {verificationStatus === 'LOADING' ? (
          <div className="verification-status-card">
            Chargement du statut de vérification...
          </div>
        ) : verificationStatus === 'PENDING' ? (
          <div className="verification-status-card is-pending">
            <FiClock />
            <h3>Vérification en cours</h3>
            <p>
              Votre CIN et votre vidéo ont bien été envoyées. Un administrateur doit maintenant les vérifier et approuver votre compte avant la création de carte ou le retrait USDT.
            </p>
            <p>La vérification prend généralement quelques minutes.</p>
            <button type="button" className="primary-btn" onClick={() => navigateTo(returnTo)}>
              Retour
            </button>
          </div>
        ) : verificationStatus === 'COMPLETED' ? (
          <div className="verification-status-card is-approved">
            <FiCheckCircle />
            <h3>Vérification approuvée</h3>
            <p>Votre compte est vérifié. Vous pouvez maintenant continuer.</p>
            <button type="button" className="primary-btn" onClick={() => {
              if (action === 'create-card') {
                sessionStorage.setItem('startCreateCard', 'true');
              }
              navigateTo(returnTo);
            }}>
              Continuer
            </button>
          </div>
        ) : showVerificationIntro ? (
          <div className="verification-intro-card">
            <div className="verification-intro-icon">
              <FiShield />
            </div>
            <h3>Vérification requise</h3>
            <p>
              Pour accéder à cette fonctionnalité, vous devez d'abord vérifier votre compte. Cette étape protège votre compte et permet de débloquer les avantages Adabo Cash.
            </p>
            <p className="verification-intro-note">
              La vérification de la CIN prend généralement quelques minutes.
            </p>
            <div className="verification-intro-list">
              <span><FiCheckCircle /> Informations personnelles</span>
              <span><FiCheckCircle /> CIN recto et verso</span>
              <span><FiCheckCircle /> Vidéo selfie en direct</span>
            </div>
            <button
              type="button"
              className="verification-action-btn verification-intro-btn"
              onClick={() => setShowVerificationIntro(false)}
            >
              Commencer la vérification
            </button>
          </div>
        ) : (
          <>
            {verificationStatus === 'FAILED' && (
              <div className="verification-status-card is-rejected">
                <FiXCircle />
                <h3>Vérification rejetée</h3>
                <p>Veuillez renvoyer une CIN lisible et une vidéo selfie claire.</p>
              </div>
            )}
            <form className="verification-page-form" onSubmit={(event) => event.preventDefault()}>
              {currentStep === 0 && (
              <div className="verification-form-section">
                <div className="verification-section-heading">
                  <span className="verification-section-kicker">Étape 1</span>
                  <h3>Informations personnelles</h3>
                  <p>Utilisez les mêmes informations que celles affichées sur votre CIN.</p>
                </div>
                <div className="verification-file-grid">
                  <label className="verification-upload-label">
                    Prénom
                    <input
                      type="text"
                      value={firstName}
                      onChange={(event) => setFirstName(event.target.value)}
                      placeholder="Votre prénom"
                      required
                    />
                  </label>
                  <label className="verification-upload-label">
                    Nom
                    <input
                      type="text"
                      value={lastName}
                      onChange={(event) => setLastName(event.target.value)}
                      placeholder="Votre nom"
                      required
                    />
                  </label>
                </div>
                <label className="verification-upload-label">
                  Date de naissance
                  <input
                    type="date"
                    value={birthDate}
                    onChange={(event) => setBirthDate(event.target.value)}
                    required
                  />
                </label>
              </div>
              )}

              {currentStep === 1 && (
              <div className="verification-form-section">
                <div className="verification-section-heading">
                  <span className="verification-section-kicker">Étape 2</span>
                  <h3>CIN</h3>
                  <p>Ajoutez une photo nette du recto et du verso de votre CIN.</p>
                </div>
                <div className="verification-file-grid">
                  <label className="verification-upload-label">
                    Recto de la CIN
                    <span className="verification-file-picker">
                      <span className="verification-file-button">Choisir un fichier</span>
                      <span className="verification-file-name">{idFront?.name || 'Aucun fichier choisi'}</span>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={(event) => setIdFront(event.target.files?.[0] || null)}
                        required
                      />
                    </span>
                  </label>
                  <label className="verification-upload-label">
                    Verso de la CIN
                    <span className="verification-file-picker">
                      <span className="verification-file-button">Choisir un fichier</span>
                      <span className="verification-file-name">{idBack?.name || 'Aucun fichier choisi'}</span>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={(event) => setIdBack(event.target.files?.[0] || null)}
                        required
                      />
                    </span>
                  </label>
                </div>
              </div>
              )}

              {currentStep === 2 && (
              <div className="verification-form-section verification-video-panel">
                <div className="verification-section-heading">
                  <span className="verification-section-kicker">Étape 3</span>
                  <h3>Vidéo selfie</h3>
                  <p>Enregistrez une courte vidéo en suivant les instructions ci-dessous.</p>
                </div>
                <div className="verification-selfie-instructions">
                  <div className="verification-selfie-instructions-layout">
                    <img
                      src={selfieImage}
                      alt="Exemple: tenir sa CIN à côté de son visage"
                      className="verification-selfie-example"
                    />
                    <div className="verification-selfie-text">
                      <h3>Instruction pour la vidéo selfie</h3>
                      <p>
                        Tenez votre CIN à côté de votre visage, puis lisez clairement la phrase ci-dessous pendant l'enregistrement.
                      </p>
                      <div className="verification-language-options" role="group" aria-label="Langue de la phrase selfie">
                        {Object.entries(SELFIE_PHRASES).map(([key, phrase]) => (
                          <button
                            key={key}
                            type="button"
                            className={`verification-language-btn${selfieLanguage === key ? ' is-active' : ''}`}
                            onClick={() => setSelfieLanguage(key)}
                          >
                            {phrase.label}
                          </button>
                        ))}
                      </div>
                      <blockquote className="verification-selfie-phrase">
                        {SELFIE_PHRASES[selfieLanguage].text}
                      </blockquote>
                    </div>
                  </div>
                </div>
                <div className="verification-recording-limit">
                  {SELFIE_DURATION_LIMIT_MESSAGE}
                </div>
                {selfieVideoUrl ? (
                  <video key={selfieVideoUrl} className="verification-video-preview" controls preload="metadata" playsInline>
                    <source src={selfieVideoUrl} type={selfieVideo?.type || 'video/webm'} />
                    Votre navigateur ne peut pas prévisualiser cette vidéo.
                  </video>
                ) : (
                  <video ref={videoRef} className="verification-video-preview" autoPlay muted playsInline />
                )}
                {cameraError && <div className="verification-error">{cameraError}</div>}
                {selfieVideo && !isRecording && (
                  <div className="verification-success">Vidéo selfie enregistrée. Vous pouvez la prévisualiser ci-dessus.</div>
                )}
                <div className="verification-record-actions">
                  {!isRecording ? (
                    <button type="button" className="verification-action-btn" onClick={startRecording}>
                      {selfieVideo ? 'Réenregistrer la vidéo' : 'Enregistrer la vidéo selfie'}
                    </button>
                  ) : (
                    <button type="button" className="verification-action-btn is-danger" onClick={stopRecording}>
                      Arrêter l'enregistrement
                    </button>
                  )}
                </div>
              </div>
              )}

              {currentStep === 3 && (
              <div className="verification-form-section">
                <div className="verification-section-heading">
                  <span className="verification-section-kicker">Étape 4</span>
                  <h3>Résumé de la vérification</h3>
                  <p>Vérifiez que tout est prêt avant d'envoyer votre demande.</p>
                </div>
                <div className="verification-summary-grid">
                  <div className="verification-summary-item">
                    <span>Nom complet</span>
                    <strong>{`${firstName} ${lastName}`.trim() || 'Non renseigné'}</strong>
                  </div>
                  <div className="verification-summary-item">
                    <span>Date de naissance</span>
                    <strong>{birthDate || 'Non renseignée'}</strong>
                  </div>
                  <div className="verification-summary-item">
                    <span>Recto CIN</span>
                    <strong>{idFront?.name || 'Non ajouté'}</strong>
                  </div>
                  <div className="verification-summary-item">
                    <span>Verso CIN</span>
                    <strong>{idBack?.name || 'Non ajouté'}</strong>
                  </div>
                  <div className="verification-summary-item">
                    <span>Vidéo selfie</span>
                    <strong>{selfieVideo ? 'Enregistrée' : 'Non enregistrée'}</strong>
                  </div>
                  <div className="verification-summary-item">
                    <span>Langue de la phrase</span>
                    <strong>{SELFIE_PHRASES[selfieLanguage].label}</strong>
                  </div>
                </div>
              </div>
              )}

              <div className="verification-wizard-actions">
                {currentStep > 0 && (
                  <button type="button" className="verification-action-btn is-secondary" onClick={goToPreviousStep} disabled={isUploading}>
                    Retour
                  </button>
                )}
                {currentStep < VERIFICATION_STEPS.length - 1 ? (
                  <button
                    type="button"
                    className="verification-action-btn verification-submit-btn"
                    onClick={(event) => {
                      event.preventDefault();
                      goToNextStep();
                    }}
                    disabled={isRecording}
                  >
                    Continuer
                  </button>
                ) : (
                  <button type="button" className="verification-action-btn verification-submit-btn" onClick={handleSubmit} disabled={isUploading || isRecording}>
                    {isUploading ? 'Envoi...' : 'Confirmer et envoyer'}
                  </button>
                )}
              </div>
            </form>
          </>
        )}
      </div>
    </section>
  );
};

export default VerificationPage;
